/* LIORME charm bracelet: a live 2D physics chain (Matter.js, loaded from cdnjs on first use), like the one on
   sarahandsebastian.com's charm bracelet builder. A ring of links tied by constraints hangs from a fixed clasp over
   an invisible "wrist" circle; each charm is a weighted body hung from a link in the lower half, so adding or
   removing one makes the chain swing and settle. Everything is drawn in code (gold or silver), charms are round
   medallions with the product photo.

   const chain = LiormeChain.create(canvas, { slots: 4, zoom: 1.6 });
   chain.setCharms([{ key, img | letter | gem, slot, button, crop }]);  // letter: metal glyph; gem: {shape, color} stone;
                                                              // button (optional) follows its charm; crop zooms the photo
   chain.setSilver(true);                            // metal colour
   LiormeChain.gemImage(gem, silver) → data URL of a gem, for buttons.
   The canvas fills its parent as the largest square that fits. The chain can be dragged. */
(() => {
  if (window.LiormeChain) return;
  const MATTER = 'https://cdnjs.cloudflare.com/ajax/libs/matter-js/0.20.0/matter.min.js';
  let loading = null;
  const loadMatter = () => {
    if (window.Matter) return Promise.resolve(window.Matter);
    if (!loading) {
      loading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = MATTER;
        s.onload = () => resolve(window.Matter);
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return loading;
  };

  const W = 800, CX = 400, CY = 360, R = 150; /* world units; the view is centred on VX,VY */
  const VX = 400, VY = 390;
  const GOLD = ['#F7E3AE', '#CFA652', '#8F6A22'];
  const SILVER = ['#FAFAFA', '#C4C7CB', '#80858C'];

  /* ---------- gems: a cut stone in a 4-prong setting (or a pearl), drawn at radius r around (0,0) ---------- */
  const shade = (hex, k) => {
    const n = parseInt(hex.slice(1), 16), c = [n >> 16, (n >> 8) & 255, n & 255];
    return 'rgb(' + c.map((v) => Math.round(k > 0 ? v + (255 - v) * k : v * (1 + k))).join(',') + ')';
  };
  const EXTENT = { round: [1, 1], oval: [0.72, 1], pear: [0.72, 1], marquise: [0.5, 1], emerald: [0.72, 1], princess: [0.82, 0.82], heart: [0.95, 0.9] };
  function gemPath(ctx, shape, r) {
    ctx.beginPath();
    if (shape === 'oval') ctx.ellipse(0, 0, r * 0.72, r, 0, 0, Math.PI * 2);
    else if (shape === 'pear') { ctx.moveTo(0, -r); ctx.bezierCurveTo(r * 0.8, -r * 0.15, r * 0.85, r, 0, r); ctx.bezierCurveTo(-r * 0.85, r, -r * 0.8, -r * 0.15, 0, -r); }
    else if (shape === 'marquise') { ctx.moveTo(0, -r); ctx.quadraticCurveTo(r, 0, 0, r); ctx.quadraticCurveTo(-r, 0, 0, -r); }
    else if (shape === 'emerald') { const w = r * 0.72, c = r * 0.24; ctx.moveTo(-w + c, -r); ctx.lineTo(w - c, -r); ctx.lineTo(w, -r + c); ctx.lineTo(w, r - c); ctx.lineTo(w - c, r); ctx.lineTo(-w + c, r); ctx.lineTo(-w, r - c); ctx.lineTo(-w, -r + c); ctx.closePath(); }
    else if (shape === 'princess') { const w = r * 0.82; ctx.rect(-w, -w, w * 2, w * 2); }
    else if (shape === 'heart') { ctx.moveTo(0, r * 0.9); ctx.bezierCurveTo(-r * 1.15, r * 0.05, -r * 0.85, -r * 1.05, 0, -r * 0.4); ctx.bezierCurveTo(r * 0.85, -r * 1.05, r * 1.15, r * 0.05, 0, r * 0.9); }
    else ctx.arc(0, 0, r, 0, Math.PI * 2);
  }
  function drawGem(ctx, gem, r, metal) {
    ctx.save();
    if (gem.shape === 'pearl') {
      const g = ctx.createRadialGradient(-r * 0.35, -r * 0.4, r * 0.05, 0, 0, r * 1.05);
      g.addColorStop(0, '#FFFFFF'); g.addColorStop(0.55, '#F1EBE1'); g.addColorStop(1, '#C9BFB0');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = metal[1]; ctx.beginPath(); ctx.ellipse(0, -r * 0.95, r * 0.28, r * 0.14, 0, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      return;
    }
    const light = parseInt(gem.color.slice(1, 3), 16) + parseInt(gem.color.slice(3, 5), 16) + parseInt(gem.color.slice(5, 7), 16) > 600;
    /* bezel */
    gemPath(ctx, gem.shape, r * 1.08);
    ctx.fillStyle = metal[1]; ctx.fill();
    /* stone */
    gemPath(ctx, gem.shape, r);
    const g = ctx.createRadialGradient(-r * 0.3, -r * 0.35, 0, 0, 0, r * 1.25);
    g.addColorStop(0, shade(gem.color, 0.7)); g.addColorStop(0.45, gem.color); g.addColorStop(1, shade(gem.color, -0.45));
    ctx.fillStyle = g; ctx.fill();
    /* facets */
    ctx.save(); ctx.clip();
    ctx.strokeStyle = light ? 'rgba(110,120,130,.45)' : 'rgba(255,255,255,.28)';
    ctx.lineWidth = Math.max(0.6, r * 0.05);
    ctx.beginPath();
    for (let i = 0; i < 8; i++) { const a = (i * Math.PI) / 4 + Math.PI / 8; ctx.moveTo(Math.cos(a) * r * 0.42, Math.sin(a) * r * 0.42); ctx.lineTo(Math.cos(a) * r * 1.4, Math.sin(a) * r * 1.4); }
    ctx.stroke();
    ctx.beginPath();
    for (let i = 0; i <= 8; i++) { const a = (i * Math.PI) / 4 + Math.PI / 8; ctx[i ? 'lineTo' : 'moveTo'](Math.cos(a) * r * 0.42, Math.sin(a) * r * 0.42); }
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    ctx.beginPath(); ctx.ellipse(-r * 0.32, -r * 0.4, r * 0.22, r * 0.12, -0.6, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    /* prongs */
    const [ex, ey] = EXTENT[gem.shape] || [1, 1];
    ctx.fillStyle = metal[0]; ctx.strokeStyle = metal[2]; ctx.lineWidth = Math.max(0.5, r * 0.04);
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x * ex * r * 0.66, y * ey * r * 0.66, r * 0.15, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); });
    ctx.restore();
  }
  const gemCache = {};
  /* a gem as an image (data URL) for buttons and slots */
  function gemImage(gem, silver, px) {
    px = px || 96;
    const key = gem.shape + gem.color + (silver ? 's' : 'g') + px;
    if (!gemCache[key]) {
      const c = document.createElement('canvas');
      c.width = c.height = px;
      const x = c.getContext('2d');
      x.translate(px / 2, px / 2);
      drawGem(x, gem, px * 0.34, silver ? SILVER : GOLD);
      gemCache[key] = c.toDataURL();
    }
    return gemCache[key];
  }

  function create(canvas, opts) {
    opts = Object.assign({ links: 84, slots: 4, zoom: 1 }, opts);
    const ctx = canvas.getContext('2d');
    const font = getComputedStyle(canvas).fontFamily;
    const imgs = {};
    const image = (src) => {
      if (!src) return null;
      if (!imgs[src]) { const i = new Image(); i.crossOrigin = 'anonymous'; i.src = src; imgs[src] = i; }
      return imgs[src];
    };
    let M = null, engine = null, links = [], charms = [], s = 10, want = [], silver = false, palette = null;
    let side = 0, dpr = 1, visible = true, raf = 0, last = 0, acc = 0, drag = null;

    /* square canvas, as large as the parent allows */
    const fit = () => {
      const p = canvas.parentElement;
      side = Math.max(1, Math.floor(Math.min(p.clientWidth, p.clientHeight)));
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.style.width = canvas.style.height = side + 'px';
      canvas.width = canvas.height = Math.round(side * dpr);
      setPalette();
    };
    const scale = () => (side / W) * opts.zoom;
    const toWorld = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: (e.clientX - r.left - (VX / W) * side) / scale() + VX, y: (e.clientY - r.top - (VY / W) * side) / scale() + VY };
    };
    const toScreen = (p) => ({ x: (p.x - VX) * scale() + (VX / W) * side, y: (p.y - VY) * scale() + (VY / W) * side });

    function setPalette() {
      const c = silver ? SILVER : GOLD;
      const g = ctx.createLinearGradient(-8, -6, 8, 6);
      g.addColorStop(0, c[0]); g.addColorStop(0.5, c[1]); g.addColorStop(1, c[2]);
      palette = { c, grad: g };
    }

    const rot = (p, a) => ({ x: p.x * Math.cos(a) - p.y * Math.sin(a), y: p.x * Math.sin(a) + p.y * Math.cos(a) });

    function build() {
      M.Composite.clear(engine.world, false);
      const n = opts.links;
      s = (2 * Math.PI * R) / n;
      links = [];
      for (let i = 0; i < n; i++) {
        const th = -Math.PI / 2 + (2 * Math.PI * i) / n;
        const top = i < n * 0.36 || i > n * 0.64; /* all but the bottom ~quarter rests on the wrist */
        links.push(M.Bodies.rectangle(CX + R * Math.cos(th), CY + R * Math.sin(th), s, s * 0.55, {
          angle: th + Math.PI / 2, friction: 0, frictionAir: 0.04, restitution: 0, density: 0.02,
          collisionFilter: top ? { category: 2, mask: 1 } : { category: 4, mask: 0 },
        }));
      }
      M.Body.setStatic(links[0], true);
      const cons = links.map((l, i) => {
        const b = links[(i + 1) % n];
        return M.Constraint.create({ bodyA: l, bodyB: b, pointA: rot({ x: s / 2, y: 0 }, l.angle), pointB: rot({ x: -s / 2, y: 0 }, b.angle), length: 0, stiffness: 0.96, damping: 0.22 });
      });
      const wrist = M.Bodies.circle(CX, CY, R - s * 0.35, { isStatic: true, collisionFilter: { category: 1, mask: 2 } });
      M.Composite.add(engine.world, [...links, ...cons, wrist]);
      charms = [];
      sync();
    }

    /* Charms hang from fixed positions spread over the lower half (slot 0 on the left). Kept charms glide to their
       link (the constraint stiffens over ~0.7s); new ones drop in from above so the chain takes the jolt. */
    function sync() {
      if (!engine) return;
      const n = links.length;
      const next = [];
      charms.forEach((c) => M.Composite.remove(engine.world, c.con));
      want.forEach((item) => {
        const k = Math.round((3 * n) / 4 - ((item.slot + 1) * (n / 2)) / (opts.slots + 1)) % n;
        const link = links[k];
        let c = charms.find((x) => x.key === item.key && !next.includes(x));
        const size = item.size || (item.letter ? 36 : item.gem ? 26 : 46);
        if (!c) {
          const body = M.Bodies.rectangle(link.position.x, link.position.y - 40, size, size, { frictionAir: 0.03, density: 0.002, collisionFilter: { category: 4, mask: 0 } });
          c = { key: item.key, body, size };
          M.Composite.add(engine.world, body);
        }
        c.item = item;
        c.con = M.Constraint.create({ bodyA: link, bodyB: c.body, pointA: { x: 0, y: 0 }, pointB: rot({ x: 0, y: -size / 2 - 5 }, c.body.angle), length: 0, stiffness: 0.03, damping: 0.1 });
        c.ramp = 0;
        M.Composite.add(engine.world, c.con);
        next.push(c);
      });
      charms.filter((c) => !next.includes(c)).forEach((c) => M.Composite.remove(engine.world, c.body));
      charms = next;
      links.slice(1).forEach((l) => M.Body.setVelocity(l, { x: l.velocity.x + (Math.random() - 0.5) * 2, y: l.velocity.y + 1.5 }));
    }

    /* drawing */
    function drawLink(b, i) {
      ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
      if (i % 2 === 0) {
        ctx.strokeStyle = palette.grad; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.ellipse(0, 0, s * 0.68, s * 0.4, 0, 0, Math.PI * 2); ctx.stroke();
      } else {
        ctx.fillStyle = palette.c[2];
        ctx.beginPath(); ctx.roundRect(-s * 0.72, -1.3, s * 1.44, 2.6, 1.3); ctx.fill();
        ctx.fillStyle = palette.c[0]; ctx.fillRect(-s * 0.5, -0.5, s, 0.8);
      }
      ctx.restore();
    }
    function drawClasp(b) {
      ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
      ctx.fillStyle = palette.grad; ctx.strokeStyle = palette.c[2]; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.roundRect(-s * 1.1, -s * 0.55, s * 2.2, s * 1.1, s * 0.55); ctx.fill(); ctx.stroke();
      ctx.restore();
    }
    function drawCharm(c) {
      const b = c.body, h = c.size / 2;
      ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
      ctx.strokeStyle = palette.grad; ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.arc(0, -h - 3, 3.2, 0, Math.PI * 2); ctx.stroke();
      if (c.item.gem) {
        drawGem(ctx, c.item.gem, h, palette.c);
        ctx.restore();
        return;
      }
      if (c.item.letter) {
        ctx.fillStyle = palette.grad;
        ctx.font = '500 ' + c.size * 1.25 + 'px ' + font;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,.18)'; ctx.shadowBlur = 3; ctx.shadowOffsetY = 1;
        ctx.fillText(c.item.letter, 0, 2);
        ctx.restore();
        return;
      }
      ctx.save();
      ctx.beginPath(); ctx.arc(0, 0, h, 0, Math.PI * 2); ctx.clip();
      ctx.fillStyle = '#F4F1EB'; ctx.fill();
      const im = image(c.item.img);
      if (im && im.complete && im.naturalWidth) {
        const sc = Math.max((h * 2) / im.naturalWidth, (h * 2) / im.naturalHeight) * (c.item.crop || 1); /* crop > 1 zooms into the photo's centre */
        ctx.drawImage(im, (-im.naturalWidth * sc) / 2, (-im.naturalHeight * sc) / 2, im.naturalWidth * sc, im.naturalHeight * sc);
      }
      ctx.restore();
      ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 0, h, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
    }

    function frame(t) {
      raf = requestAnimationFrame(frame);
      if (!visible || document.hidden || !side) { last = t; return; }
      const dt = Math.min(t - last || 16.7, 33);
      last = t;
      charms.forEach((c) => { if (c.ramp < 1) { c.ramp = Math.min(1, c.ramp + dt / 700); c.con.stiffness = 0.03 + 0.87 * c.ramp * c.ramp; } });
      for (acc += dt; acc >= 1000 / 60; acc -= 1000 / 60) M.Engine.update(engine, 1000 / 60);
      const k = dpr * scale();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(k, 0, 0, k, dpr * ((VX / W) * side - VX * scale()), dpr * ((VY / W) * side - VY * scale()));
      links.forEach((b, i) => { if (i) drawLink(b, i); });
      drawClasp(links[0]);
      charms.forEach(drawCharm);
      charms.forEach((c) => {
        const btn = c.item.button;
        if (!btn) return;
        const p = toScreen({ x: c.body.position.x + c.size * 0.42, y: c.body.position.y - c.size * 0.42 });
        btn.style.transform = 'translate(' + (canvas.offsetLeft + p.x) + 'px,' + (canvas.offsetTop + p.y) + 'px) translate(-50%,-50%)';
      });
    }

    /* drag the chain or a charm */
    canvas.addEventListener('pointerdown', (e) => {
      if (!engine) return;
      const p = toWorld(e);
      let best = null, bd = 34 / opts.zoom;
      [...links.slice(1), ...charms.map((c) => c.body)].forEach((b) => { const d = Math.hypot(b.position.x - p.x, b.position.y - p.y); if (d < bd) { bd = d; best = b; } });
      if (!best) return;
      e.preventDefault();
      drag = M.Constraint.create({ pointA: p, bodyB: best, pointB: { x: 0, y: 0 }, length: 0, stiffness: 0.08, damping: 0.1 });
      M.Composite.add(engine.world, drag);
      canvas.setPointerCapture(e.pointerId);
      canvas.classList.add('is-dragging');
    });
    canvas.addEventListener('pointermove', (e) => { if (drag) drag.pointA = toWorld(e); });
    const drop = () => { if (drag) { M.Composite.remove(engine.world, drag); drag = null; canvas.classList.remove('is-dragging'); } };
    canvas.addEventListener('pointerup', drop);
    canvas.addEventListener('pointercancel', drop);

    new ResizeObserver(fit).observe(canvas.parentElement);
    new IntersectionObserver((en) => { visible = en[0].isIntersecting; }).observe(canvas);
    fit();
    loadMatter().then((Matter) => {
      M = Matter;
      engine = M.Engine.create({ constraintIterations: 18, positionIterations: 12, velocityIterations: 8 });
      build();
      raf = requestAnimationFrame(frame);
    }).catch(() => {});

    return {
      setCharms(list) { want = list.slice().sort((a, b) => a.slot - b.slot); sync(); },
      setSilver(on) { if (silver !== !!on) { silver = !!on; setPalette(); } },
    };
  }

  window.LiormeChain = { create, gemImage, preload: () => loadMatter().catch(() => {}) };
})();
